import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Plus, Trash2, Settings, List, Upload, FileText, X, Image, CheckCircle, AlertCircle, Loader2, Edit2 } from 'lucide-react';
import { contentAPI, getMediaUrl } from '../services/api';
import { FileUploader } from './FileUploader';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Points system for TCF Canada
const POINTS_BY_LEVEL = {
  'A1': 3,
  'A2': 9,
  'B1': 15,
  'B2': 21,
  'C1': 26,
  'C2': 33
};

// Level assignment by question number
const getLevelByQuestion = (questionNum) => {
  if (questionNum >= 1 && questionNum <= 4) return 'A1';
  if (questionNum >= 5 && questionNum <= 10) return 'A2';
  if (questionNum >= 11 && questionNum <= 19) return 'B1';
  if (questionNum >= 20 && questionNum <= 29) return 'B2';
  if (questionNum >= 30 && questionNum <= 35) return 'C1';
  if (questionNum >= 36 && questionNum <= 39) return 'C2';
  return 'A1';
};

export const ComprehensionEcriteManager = () => {
  const [series, setSeries] = useState([]);
  const [selectedSerie, setSelectedSerie] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [imageFiles, setImageFiles] = useState({});
  
  // Image upload states
  const [uploadedImageFiles, setUploadedImageFiles] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const imageInputRef = useRef(null);
  
  // Edit mode states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Question form state
  const [formData, setFormData] = useState({
    instruction: '',
    texte: '',
    imageUrl: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: 'A'
  });

  useEffect(() => {
    loadSeries();
  }, []);

  // Load uploaded files when serie changes
  useEffect(() => {
    if (selectedSerie?.id) {
      loadUploadedImageFiles(selectedSerie.id);
    }
  }, [selectedSerie?.id]);

  const loadSeries = async () => {
    try {
      const loadedSeries = await contentAPI.getSeries('comprehension_ecrite');
      setSeries(Array.isArray(loadedSeries) ? loadedSeries : []);
    } catch (error) {
      console.error('Error loading series:', error);
      toast.error('Erreur lors du chargement des séries');
    }
  };

  const loadUploadedImageFiles = async (blocId) => {
    try {
      const response = await fetch(`${API_URL}/api/upload/images/${blocId}`);
      if (response.ok) {
        const data = await response.json();
        setUploadedImageFiles(data.files || []);
        const imageMap = {};
        data.files.forEach(f => {
          if (f.question_number) {
            imageMap[`image_${f.question_number}`] = f.url;
          }
        });
        setImageFiles(imageMap);
      }
    } catch (error) {
      console.error('Error loading image files:', error);
    }
  };

  const handleBulkImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    if (!selectedSerie?.id) {
      toast.error('Veuillez d\'abord sélectionner un bloc');
      return;
    }

    setIsUploadingImages(true);
    setUploadErrors([]);

    const formDataUpload = new FormData();
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }

    try {
      const response = await fetch(`${API_URL}/api/upload/images/${selectedSerie.id}`, {
        method: 'POST',
        body: formDataUpload
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${data.uploaded_files.length} images uploadées !`);
        await loadUploadedImageFiles(selectedSerie.id);
      }

      if (data.errors && data.errors.length > 0) {
        setUploadErrors(prev => [...prev, ...data.errors]);
        data.errors.forEach(err => toast.error(err));
      }

    } catch (error) {
      toast.error('Erreur lors de l\'upload des images');
      console.error('Upload error:', error);
    } finally {
      setIsUploadingImages(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleCreateSerie = async () => {
    try {
      const serieNumber = series.length + 1;
      const newSerie = {
        id: `serie_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        number: serieNumber,
        name: `Bloc ${serieNumber}`,
        questions: [],
        created_at: new Date().toISOString()
      };
      const result = await contentAPI.createSerie('comprehension_ecrite', newSerie);
      if (result.success) {
        toast.success(`Bloc ${serieNumber} créé avec succès`);
        loadSeries();
      } else {
        toast.error(result.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating serie:', error);
      toast.error('Erreur lors de la création du bloc');
    }
  };

  const handleDeleteSerie = async (serieId, serieName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${serieName}" ?`)) {
      try {
        const result = await contentAPI.deleteSerie('comprehension_ecrite', serieId);
        if (result.success) {
          toast.success('Bloc supprimé');
          loadSeries();
          if (selectedSerie?.id === serieId) setSelectedSerie(null);
        } else {
          toast.error(result.message || 'Erreur lors de la suppression');
        }
      } catch (error) {
        console.error('Error deleting serie:', error);
        toast.error('Erreur lors de la suppression du bloc');
      }
    }
  };

  const handleSelectSerie = (serie) => {
    setSelectedSerie(serie);
  };

  // Parse bulk import text
  const parseBulkText = (text) => {
    const questions = [];
    const lines = text.split('\n');
    
    let currentQuestion = null;
    let collectingOptions = false;
    let currentOption = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (!line) continue;
      
      if (/^(\d{1,2})$/.test(line) && parseInt(line) >= 1 && parseInt(line) <= 39) {
        if (currentQuestion && currentQuestion.instruction) {
          questions.push(currentQuestion);
        }
        const qNum = parseInt(line);
        const level = getLevelByQuestion(qNum);
        currentQuestion = {
          number: qNum,
          level: level,
          points: POINTS_BY_LEVEL[level],
          instruction: '',
          texte: '',
          optionA: '',
          optionB: '',
          optionC: '',
          optionD: '',
          correctAnswer: 'A',
          imageUrl: ''
        };
        collectingOptions = false;
        currentOption = '';
        continue;
      }
      
      if (/^[ABC][12]$/.test(line)) continue;
      if (/^\d+pt$/.test(line)) continue;
      if (line === 'Non répondue') continue;
      if (/^Question \d+$/.test(line)) continue;
      
      if (line.startsWith('Question') && line.includes(':') && currentQuestion) {
        currentQuestion.instruction = line;
        collectingOptions = true;
        continue;
      }
      
      if (collectingOptions && currentQuestion) {
        if (line === 'Bonne réponse') {
          if (currentOption) {
            currentQuestion.correctAnswer = currentOption;
          }
          continue;
        }
        
        if (line.startsWith('A.')) {
          currentOption = 'A';
          currentQuestion.optionA = line.substring(2).trim();
          continue;
        }
        if (line.startsWith('B.')) {
          currentOption = 'B';
          currentQuestion.optionB = line.substring(2).trim();
          continue;
        }
        if (line.startsWith('C.')) {
          currentOption = 'C';
          currentQuestion.optionC = line.substring(2).trim();
          continue;
        }
        if (line.startsWith('D.')) {
          currentOption = 'D';
          currentQuestion.optionD = line.substring(2).trim();
          continue;
        }
      }
    }
    
    if (currentQuestion && currentQuestion.instruction) {
      questions.push(currentQuestion);
    }
    
    return questions.sort((a, b) => a.number - b.number);
  };

  const handleBulkImport = async () => {
    if (!selectedSerie) {
      toast.error('Veuillez d\'abord sélectionner un bloc');
      return;
    }

    const parsedQuestions = parseBulkText(bulkText);
    
    if (parsedQuestions.length === 0) {
      toast.error('Aucune question trouvée dans le texte');
      return;
    }

    // Map image files to questions
    const updatedQuestions = parsedQuestions.map(q => ({
      id: `q_${Date.now()}_${q.number}`,
      number: q.number,
      level: q.level,
      points: q.points,
      instruction: q.instruction,
      texte: q.texte || '',
      imageUrl: imageFiles[`image_${q.number}`] || '',
      optionA: q.optionA,
      optionB: q.optionB,
      optionC: q.optionC,
      optionD: q.optionD,
      correctAnswer: q.correctAnswer,
      created_at: new Date().toISOString()
    }));

    try {
      const result = await contentAPI.updateSerie('comprehension_ecrite', selectedSerie.id, {
        questions: updatedQuestions
      });

      if (result.success) {
        toast.success(`${parsedQuestions.length} questions importées avec succès !`);
        setShowBulkImport(false);
        setBulkText('');
        loadSeries();
        const updatedSerie = await contentAPI.getSerie('comprehension_ecrite', selectedSerie.id);
        setSelectedSerie(updatedSerie);
      } else {
        toast.error(result.message || 'Erreur lors de l\'import');
      }
    } catch (error) {
      console.error('Error importing questions:', error);
      toast.error('Erreur lors de l\'import');
    }
  };

  const resetQuestionForm = () => {
    setFormData({
      instruction: '',
      texte: '',
      imageUrl: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswer: 'A'
    });
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!formData.instruction) {
      toast.error('Veuillez remplir la consigne');
      return;
    }
    if (!selectedSerie) return;

    const currentQuestions = selectedSerie.questions || [];
    const newQuestionNum = currentQuestions.length + 1;
    const level = getLevelByQuestion(newQuestionNum);

    const newQuestion = {
      id: `q_${Date.now()}`,
      number: newQuestionNum,
      level: level,
      points: POINTS_BY_LEVEL[level],
      ...formData,
      created_at: new Date().toISOString()
    };

    const updatedQuestions = [...currentQuestions, newQuestion];
    
    try {
      await contentAPI.updateSerie('comprehension_ecrite', selectedSerie.id, { questions: updatedQuestions });
      toast.success('Question ajoutée');
      resetQuestionForm();
      setShowQuestionForm(false);
      loadSeries();
      const updatedSerie = await contentAPI.getSerie('comprehension_ecrite', selectedSerie.id);
      setSelectedSerie(updatedSerie);
    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Erreur lors de l\'ajout de la question');
    }
  };

  // Edit question handlers
  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setFormData({
      instruction: question.instruction || '',
      texte: question.texte || '',
      imageUrl: question.imageUrl || '',
      optionA: question.optionA || '',
      optionB: question.optionB || '',
      optionC: question.optionC || '',
      optionD: question.optionD || '',
      correctAnswer: question.correctAnswer || 'A'
    });
    setShowEditModal(true);
  };

  const handleSaveEditQuestion = async (e) => {
    e.preventDefault();
    if (!editingQuestion || !selectedSerie) return;

    const updatedQuestions = selectedSerie.questions.map(q => {
      if (q.id === editingQuestion.id) {
        return {
          ...q,
          instruction: formData.instruction,
          texte: formData.texte,
          imageUrl: formData.imageUrl,
          optionA: formData.optionA,
          optionB: formData.optionB,
          optionC: formData.optionC,
          optionD: formData.optionD,
          correctAnswer: formData.correctAnswer,
          // Keep the same number, level, and points
          number: q.number,
          level: q.level,
          points: q.points
        };
      }
      return q;
    });

    try {
      await contentAPI.updateSerie('comprehension_ecrite', selectedSerie.id, { questions: updatedQuestions });
      toast.success(`Question ${editingQuestion.number} modifiée avec succès`);
      setShowEditModal(false);
      setEditingQuestion(null);
      resetQuestionForm();
      loadSeries();
      const updatedSerie = await contentAPI.getSerie('comprehension_ecrite', selectedSerie.id);
      setSelectedSerie(updatedSerie);
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!selectedSerie) return;
    if (window.confirm('Supprimer cette question ?')) {
      const updatedQuestions = selectedSerie.questions.filter(q => q.id !== questionId);
      // Renumber questions
      updatedQuestions.forEach((q, index) => {
        q.number = index + 1;
        q.level = getLevelByQuestion(index + 1);
        q.points = POINTS_BY_LEVEL[q.level];
      });
      
      try {
        await contentAPI.updateSerie('comprehension_ecrite', selectedSerie.id, { questions: updatedQuestions });
        toast.success('Question supprimée');
        loadSeries();
        const updatedSerie = await contentAPI.getSerie('comprehension_ecrite', selectedSerie.id);
        setSelectedSerie(updatedSerie);
      } catch (error) {
        console.error('Error deleting question:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-500/10 p-3 rounded-lg">
            <BookOpen className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Compréhension Écrite</h2>
            <p className="text-sm text-muted-foreground">{series.length} bloc(s) • 39 questions par bloc • 699 points max</p>
          </div>
        </div>
        <button
          onClick={handleCreateSerie}
          data-testid="create-serie-ce-button"
          className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Créer un nouveau bloc</span>
        </button>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Series list */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="font-semibold text-lg flex items-center space-x-2">
            <List className="h-5 w-5" />
            <span>Blocs d'exercices</span>
          </h3>
          
          {series.length === 0 ? (
            <div className="bg-card p-6 rounded-xl border text-center">
              <BookOpen className="h-12 w-12 text-blue-500 mx-auto mb-3 opacity-50" />
              <p className="text-muted-foreground text-sm">Aucun bloc créé</p>
            </div>
          ) : (
            <div className="space-y-2">
              {series.map((serie) => (
                <div
                  key={serie.id}
                  className={`bg-card p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedSerie?.id === serie.id 
                      ? 'border-primary bg-primary/5 shadow-md' 
                      : 'hover:border-primary/50'
                  }`}
                  onClick={() => handleSelectSerie(serie)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{serie.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {serie.questions?.length || 0} / 39 questions
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {serie.questions?.length === 39 && (
                        <span className="text-xs bg-blue-500/10 text-blue-600 px-2 py-1 rounded-full">
                          Complet
                        </span>
                      )}
                      {serie.questions?.length > 0 && serie.questions?.length < 39 && (
                        <span className="text-xs bg-yellow-500/10 text-yellow-600 px-2 py-1 rounded-full">
                          {serie.questions.length} Q
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSerie(serie.id, serie.name);
                        }}
                        className="p-2 rounded-lg text-red-500 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${((serie.questions?.length || 0) / 39) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-4">
          {selectedSerie ? (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-semibold text-lg flex items-center space-x-2">
                  <Settings className="h-5 w-5" />
                  <span>{selectedSerie.name}</span>
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowBulkImport(!showBulkImport)}
                    data-testid="bulk-import-ce-button"
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                  >
                    <Upload className="h-5 w-5" />
                    <span>Import en masse</span>
                  </button>
                  <button
                    onClick={() => setShowQuestionForm(!showQuestionForm)}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                  >
                    <Plus className="h-5 w-5" />
                    <span>Ajouter ({selectedSerie.questions?.length || 0}/39)</span>
                  </button>
                </div>
              </div>

              {/* Bulk Import Panel */}
              {showBulkImport && (
                <div className="bg-card p-6 rounded-xl border shadow-sm animate-in">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold flex items-center space-x-2">
                      <FileText className="h-5 w-5 text-purple-500" />
                      <span>Import en masse</span>
                    </h4>
                    <button onClick={() => setShowBulkImport(false)} className="p-2 hover:bg-secondary rounded-lg">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-purple-500/10 p-4 rounded-lg text-sm">
                      <p className="font-medium text-purple-700 dark:text-purple-300 mb-2">Format attendu :</p>
                      <p className="text-muted-foreground">
                        Collez le texte avec le format : Numéro, Niveau, Points, Question, Options A-D avec "Bonne réponse" marquée.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Collez vos questions ici :</label>
                      <textarea
                        value={bulkText}
                        onChange={(e) => setBulkText(e.target.value)}
                        data-testid="bulk-text-ce-input"
                        className="w-full px-4 py-3 rounded-lg border bg-background text-sm min-h-[200px] font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        placeholder="1
A1
3pt
Non répondue
Question 1
Question 1 : Lisez le texte et choisissez la bonne réponse.
A.
B.
Bonne réponse
C.
D.
..."
                      />
                    </div>

                    {/* Image files section - BULK UPLOAD */}
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium flex items-center space-x-2">
                          <Image className="h-4 w-4 text-blue-500" />
                          <span>Images (Upload en masse)</span>
                        </label>
                        {uploadedImageFiles.length > 0 && (
                          <span className="text-xs bg-blue-500/10 text-blue-600 px-2 py-1 rounded-full">
                            {uploadedImageFiles.length} fichier(s)
                          </span>
                        )}
                      </div>
                      
                      <div className="bg-blue-500/10 p-4 rounded-lg">
                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                          <strong>Instructions :</strong> Nommez vos images avec le numéro (ex: 1.png, 2.jpg, ... 39.png)
                        </p>
                        
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleBulkImageUpload}
                          className="hidden"
                          id="bulk-image-ce-upload"
                        />
                        <label
                          htmlFor="bulk-image-ce-upload"
                          className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                            isUploadingImages 
                              ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                              : 'border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                          }`}
                        >
                          {isUploadingImages ? (
                            <>
                              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                              <span className="text-sm font-medium">Upload en cours...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-5 w-5 text-blue-500" />
                              <span className="text-sm font-medium">Sélectionner les images</span>
                            </>
                          )}
                        </label>
                      </div>

                      {uploadedImageFiles.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {uploadedImageFiles.map((file, i) => (
                            <span key={i} className="inline-flex items-center space-x-1 text-xs bg-blue-500/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                              <CheckCircle className="h-3 w-3" />
                              <span>Q{file.question_number}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Show errors */}
                    {uploadErrors.length > 0 && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                        <p className="text-xs font-medium text-red-600 mb-2 flex items-center space-x-1">
                          <AlertCircle className="h-4 w-4" />
                          <span>Erreurs :</span>
                        </p>
                        <ul className="text-xs text-red-500 space-y-1">
                          {uploadErrors.slice(0, 5).map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Preview of parsed questions */}
                    {bulkText && (
                      <div className="bg-secondary/50 p-4 rounded-lg">
                        <p className="text-sm font-medium mb-2">Aperçu du parsing :</p>
                        {(() => {
                          const parsed = parseBulkText(bulkText);
                          if (parsed.length === 0) {
                            return <p className="text-red-500 text-sm">Aucune question détectée. Vérifiez le format.</p>;
                          }
                          const totalPoints = parsed.reduce((sum, q) => sum + q.points, 0);
                          return (
                            <div className="space-y-2">
                              <p className="text-blue-600 font-semibold">{parsed.length} questions détectées • {totalPoints} points total</p>
                              <div className="flex flex-wrap gap-1">
                                {parsed.slice(0, 10).map((q, i) => (
                                  <span key={i} className={`text-xs px-2 py-1 rounded text-white ${
                                    q.level === 'A1' ? 'bg-green-500' :
                                    q.level === 'A2' ? 'bg-blue-500' :
                                    q.level === 'B1' ? 'bg-yellow-500' :
                                    q.level === 'B2' ? 'bg-orange-500' :
                                    q.level === 'C1' ? 'bg-red-500' : 'bg-purple-500'
                                  }`}>
                                    Q{q.number} ({q.level}) Rép: {q.correctAnswer}
                                  </span>
                                ))}
                                {parsed.length > 10 && <span className="text-xs px-2 py-1">... et {parsed.length - 10} autres</span>}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    <div className="flex space-x-3 pt-2">
                      <button
                        onClick={handleBulkImport}
                        data-testid="confirm-bulk-import-ce"
                        className="flex-1 px-6 py-3 rounded-lg bg-purple-500 text-white hover:bg-purple-600 font-semibold"
                      >
                        Importer les questions
                      </button>
                      <button
                        onClick={() => { setShowBulkImport(false); setBulkText(''); setUploadErrors([]); }}
                        className="px-6 py-3 rounded-lg border hover:bg-secondary"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Single Question Form */}
              {showQuestionForm && !showBulkImport && (
                <div className="bg-card p-6 rounded-xl border shadow-sm animate-in">
                  <h4 className="text-lg font-semibold mb-4">Nouvelle Question (Q{(selectedSerie.questions?.length || 0) + 1})</h4>
                  <form onSubmit={handleAddQuestion} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Consigne *</label>
                      <textarea
                        value={formData.instruction}
                        onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border bg-background text-sm min-h-[60px]"
                        placeholder="Ex: Lisez le texte et choisissez la bonne réponse"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Texte à lire (optionnel)</label>
                      <textarea
                        value={formData.texte}
                        onChange={(e) => setFormData({ ...formData, texte: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border bg-background text-sm min-h-[100px]"
                        placeholder="Entrez le texte de lecture..."
                      />
                    </div>

                    <FileUploader
                      onFileSelect={(data) => setFormData({ ...formData, imageUrl: data || '' })}
                      accept="image/*"
                      label="Image (optionnel)"
                      fileType="image"
                    />

                    <div className="grid grid-cols-2 gap-4">
                      {['A', 'B', 'C', 'D'].map((opt) => (
                        <div key={opt} className="space-y-2">
                          <label className="text-sm font-medium">Option {opt}</label>
                          <input
                            type="text"
                            value={formData[`option${opt}`]}
                            onChange={(e) => setFormData({ ...formData, [`option${opt}`]: e.target.value })}
                            className="w-full h-10 px-3 rounded-lg border bg-background text-sm"
                          />
                        </div>
                      ))}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Bonne réponse</label>
                      <div className="flex space-x-4">
                        {['A', 'B', 'C', 'D'].map((opt) => (
                          <label key={opt} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="correctAnswer"
                              checked={formData.correctAnswer === opt}
                              onChange={() => setFormData({ ...formData, correctAnswer: opt })}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <button type="submit" className="flex-1 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold">
                        Ajouter
                      </button>
                      <button type="button" onClick={() => setShowQuestionForm(false)} className="px-6 py-3 rounded-lg border">
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Edit Question Modal */}
              {showEditModal && editingQuestion && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-card p-6 rounded-xl border shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold flex items-center space-x-2">
                        <Edit2 className="h-5 w-5 text-blue-500" />
                        <span>Modifier Question {editingQuestion.number}</span>
                        <span className={`text-xs px-2 py-1 rounded text-white ${
                          editingQuestion.level === 'A1' ? 'bg-green-500' :
                          editingQuestion.level === 'A2' ? 'bg-blue-500' :
                          editingQuestion.level === 'B1' ? 'bg-yellow-500' :
                          editingQuestion.level === 'B2' ? 'bg-orange-500' :
                          editingQuestion.level === 'C1' ? 'bg-red-500' : 'bg-purple-500'
                        }`}>
                          {editingQuestion.level} • {editingQuestion.points}pt
                        </span>
                      </h4>
                      <button onClick={() => { setShowEditModal(false); setEditingQuestion(null); resetQuestionForm(); }} className="p-2 hover:bg-secondary rounded-lg">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    
                    <form onSubmit={handleSaveEditQuestion} className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Consigne *</label>
                        <textarea
                          value={formData.instruction}
                          onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border bg-background text-sm min-h-[60px]"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Texte à lire</label>
                        <textarea
                          value={formData.texte}
                          onChange={(e) => setFormData({ ...formData, texte: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border bg-background text-sm min-h-[60px]"
                        />
                      </div>

                      <FileUploader
                        onFileSelect={(data) => setFormData({ ...formData, imageUrl: data || '' })}
                        accept="image/*"
                        label="Image (optionnel)"
                        fileType="image"
                        currentValue={formData.imageUrl}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        {['A', 'B', 'C', 'D'].map((opt) => (
                          <div key={opt} className="space-y-2">
                            <label className="text-sm font-medium">Option {opt}</label>
                            <input
                              type="text"
                              value={formData[`option${opt}`]}
                              onChange={(e) => setFormData({ ...formData, [`option${opt}`]: e.target.value })}
                              className="w-full h-10 px-3 rounded-lg border bg-background text-sm"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Bonne réponse</label>
                        <div className="flex space-x-4">
                          {['A', 'B', 'C', 'D'].map((opt) => (
                            <label key={opt} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="editCorrectAnswer"
                                checked={formData.correctAnswer === opt}
                                onChange={() => setFormData({ ...formData, correctAnswer: opt })}
                              />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex space-x-3 pt-4 border-t">
                        <button type="submit" className="flex-1 px-6 py-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 font-semibold">
                          Enregistrer les modifications
                        </button>
                        <button type="button" onClick={() => { setShowEditModal(false); setEditingQuestion(null); resetQuestionForm(); }} className="px-6 py-3 rounded-lg border hover:bg-secondary">
                          Annuler
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Questions list */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {selectedSerie.questions?.length === 0 ? (
                  <div className="bg-card p-8 rounded-xl border text-center">
                    <p className="text-muted-foreground">Aucune question</p>
                    <p className="text-sm text-muted-foreground mt-1">Utilisez l'import en masse ou ajoutez des questions une par une</p>
                  </div>
                ) : (
                  selectedSerie.questions?.map((question) => (
                    <div key={question.id} className="bg-card p-3 rounded-lg border hover:border-primary/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${
                            question.level === 'A1' ? 'bg-green-500' :
                            question.level === 'A2' ? 'bg-blue-500' :
                            question.level === 'B1' ? 'bg-yellow-500' :
                            question.level === 'B2' ? 'bg-orange-500' :
                            question.level === 'C1' ? 'bg-red-500' :
                            'bg-purple-500'
                          }`}>
                            {question.number}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm line-clamp-1">{question.instruction}</p>
                            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                              <span>{question.level} • {question.points}pt • Rép: {question.correctAnswer}</span>
                              {question.imageUrl && <span className="text-blue-500">🖼️</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <button
                            onClick={() => handleEditQuestion(question)}
                            className="p-2 rounded text-blue-500 hover:bg-blue-500/10"
                            title="Modifier cette question"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="p-2 rounded text-red-500 hover:bg-red-500/10"
                            title="Supprimer cette question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="bg-card p-12 rounded-xl border text-center">
              <BookOpen className="h-16 w-16 text-blue-500 mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground">Sélectionnez un bloc pour gérer ses questions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
